/**
 * useRestOnce — SWR-enabled one-shot REST fetch with shared dataCache.
 *
 * Stale-While-Revalidate: shows cached data INSTANTLY (even if stale),
 * then revalidates in background. Result: perceived 0ms load on warm paths.
 *
 * Uses the canonical dataCache (L1 memory + L2 sessionStorage).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as cache from '@/lib/dataCache';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const DEFAULT_TTL = 120_000; // 2 min fresh window
const SWR_MAX_AGE = 10 * 60 * 1000; // 10 min stale window for SWR

export function useRestOnce(path, {
  skip = false,
  ttl = DEFAULT_TTL,
  cacheKey = null,
  transform = null,
  defaultValue = null,
} = {}) {
  const key = cacheKey || path;

  // SWR: check fresh first, then stale
  const fresh = key ? cache.getFresh(key, ttl) : undefined;
  const stale = fresh === undefined && key ? cache.getStale(key, SWR_MAX_AGE) : undefined;
  const initial = fresh ?? stale ?? defaultValue;
  const hasFresh = fresh !== undefined;
  const hasStale = stale !== undefined;

  const [data, setData] = useState(initial);
  // Only show loading spinner if we have NO data (fresh or stale)
  const [loading, setLoading] = useState(!hasFresh && !hasStale && !skip);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const doFetch = useCallback(async (force = false) => {
    if (skip || !path) return;

    // If fresh data exists and not forcing, skip fetch entirely
    if (!force && key) {
      const c = cache.getFresh(key, ttl);
      if (c !== undefined) {
        setData(c);
        setLoading(false);
        return;
      }
    }

    if (force) {
      setRefreshing(true);
    } else if (!hasStale) {
      // Only show loading if no stale data to show
      setLoading(true);
    }
    try {
      const url = path.startsWith('http') ? path : `${API}${path.startsWith('/') ? '' : '/'}${path}`;
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(url, { headers, timeout: 30000 });
      const result = transformRef.current ? transformRef.current(res.data) : res.data;
      if (key) {
        cache.set(key, result);
      }
      setData(result);
      setError(null);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [path, skip, key, ttl, hasStale]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { data, loading, refreshing, error, refresh: () => doFetch(true) };
}

/**
 * useMultiRestOnce — parallel one-shot REST fetch for multiple endpoints.
 */
export function useMultiRestOnce(pathMap, { skip = false, ttl = DEFAULT_TTL } = {}) {
  const entries = Object.entries(pathMap || {});
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(!skip && entries.length > 0);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const mapKey = entries.map(([k, v]) => `${k}:${v}`).join('|');

  const doFetch = useCallback(async (force = false) => {
    if (skip || entries.length === 0) return;

    if (!force) {
      const allCached = entries.every(([k]) => {
        return cache.getFresh(`multi:${k}`, ttl) !== undefined;
      });
      if (allCached) {
        const cached = {};
        entries.forEach(([k]) => { cached[k] = cache.get(`multi:${k}`); });
        setResults(cached);
        setLoading(false);
        return;
      }
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const promises = entries.map(([, path]) =>
        axios.get(`${API}${path.startsWith('/') ? '' : '/'}${path}`, { signal: controller.signal, headers, timeout: 15000 })
          .catch(() => null)
      );
      const responses = await Promise.all(promises);
      const newResults = {};
      entries.forEach(([k], i) => {
        const d = responses[i]?.data ?? null;
        newResults[k] = d;
        cache.set(`multi:${k}`, d);
      });
      setResults(newResults);
      setError(null);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [mapKey, skip, ttl]);

  useEffect(() => {
    doFetch();
    return () => abortRef.current?.abort();
  }, [doFetch]);

  return { results, loading, error, refresh: () => doFetch(true) };
}
